import { useState, useEffect, useCallback } from 'react';
import { AppConfig, AppConfigParameter } from '../types';
import { useConnection } from '../context/ConnectionContext';
import { fetchAppConfigApi, updateAppConfigParameterApi } from '../services/configApiClient';

export interface UpdateConfigStatus {
  loading: boolean;
  error: string | null;
  success: boolean;
}

export const useAppConfig = () => {
  const [appConfig, setAppConfig] = useState<AppConfig | null>(null);
  const [configLoading, setConfigLoading] = useState(true);
  const [configError, setConfigError] = useState<string | null>(null);
  const [updateStatus, setUpdateStatus] = useState<{ [parameterId: string]: UpdateConfigStatus }>({});
  const { setIsOffline } = useConnection();

  const loadConfig = useCallback(async () => {
    try {
      setConfigLoading(true);
      const data = await fetchAppConfigApi();
      setAppConfig(data);
      setConfigLoading(false);
      setConfigError(null);
      setIsOffline(false);
    } catch (error: any) {
      console.error('Error fetching app config from API:', error);
      setConfigError(error?.message || 'Error al cargar la configuración');
      setAppConfig(null);
      setConfigLoading(false);
    }
  }, [setIsOffline]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const isFeatureEnabled = (featureKey: keyof AppConfig): boolean => {
    if (configLoading) return false;
    if (configError || !appConfig || !appConfig[featureKey]) return true;
    const feature = appConfig[featureKey];
    return feature?.enabled === true;
  };

  const getAppVersion = (): string => {
    if (configLoading || configError || !appConfig || !appConfig.app_version_info) {
      return '0.0.0';
    }
    const versionInfo = appConfig.app_version_info as { current_version?: string };
    return versionInfo.current_version || '0.0.0';
  };

  const updateAppConfigParameter = useCallback(
    async (parameterId: string, newValues: Partial<AppConfigParameter>): Promise<boolean> => {
      setUpdateStatus((prev) => ({ ...prev, [parameterId]: { loading: true, error: null, success: false } }));
      try {
        await updateAppConfigParameterApi(parameterId, newValues);
        setAppConfig((prev) => {
          if (!prev) return prev;
          const currentParam = prev[parameterId] || { id: parameterId };
          return {
            ...prev,
            [parameterId]: {
              ...currentParam,
              ...newValues,
            },
          };
        });
        setUpdateStatus((prev) => ({ ...prev, [parameterId]: { loading: false, error: null, success: true } }));
        return true;
      } catch (error: any) {
        console.error('Error updating app config parameter:', error);
        setUpdateStatus((prev) => ({ ...prev, [parameterId]: { loading: false, error: error?.message || 'Error al guardar', success: false } }));
        return false;
      }
    },
    []
  );

  return { appConfig, configLoading, configError, isFeatureEnabled, getAppVersion, updateAppConfigParameter, updateStatus };
};
